import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Carrier, CreateCarrierInput } from '@magimp/shared';
import {
  Button,
  DataTable,
  EditorModal,
  EmptyState,
  ErrorState,
  Input,
  LoadingState,
  Select,
  Textarea,
  ToolbarActions,
  useEditorGuard,
} from '../../../ui/index.js';
import { createCarrier, listCarriers } from '../../../api/requests/index.js';
import { errorMessage } from '../../../api/errors.js';
import { useToastStore } from '../../../stores/toast.js';
import { CarrierEditorModal } from './CarrierEditorModal.js';

type CarrierFilter = 'active' | 'inactive' | 'all';

export function CarriersPage() {
  const { t } = useTranslation(['carriers', 'common']);
  const [filter, setFilter] = useState<CarrierFilter>('active');
  const [q, setQ] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [selected, setSelected] = useState<Carrier | null>(null);
  const query = useQuery({
    queryKey: ['carriers-catalog', filter, q],
    queryFn: () => listForFilter(filter, q),
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, minHeight: 0, flex: 1 }}>
      <header style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <h1 style={{ fontSize: 20, fontWeight: 600, margin: 0, flex: 1 }}>{t('carriers:title')}</h1>
        <ToolbarActions>
          <Input value={q} onChange={setQ} icon="search" placeholder={t('carriers:search')} />
          <Select
            value={filter}
            onChange={(v) => setFilter(v as CarrierFilter)}
            options={[
              { value: 'active', label: t('filters.active') },
              { value: 'inactive', label: t('filters.inactive') },
              { value: 'all', label: t('filters.all') },
            ]}
            style={{ width: 150 }}
          />
          <Button kind="primary" icon="plus" onClick={() => setCreateOpen(true)}>
            {t('actions.create')}
          </Button>
        </ToolbarActions>
      </header>
      {query.error && (
        <ErrorState
          title={t('common:states.loadError')}
          text={errorMessage(query.error)}
          onRetry={() => void query.refetch()}
        />
      )}
      {query.isPending && <LoadingState />}
      {query.isSuccess && (
        <DataTable
          rows={query.data}
          onSelect={setSelected}
          emptyState={<EmptyState title={t('common:states.emptyTitle')} text={t('empty')} />}
          columns={[
            { key: 'name', label: t('columns.name'), sortable: true },
            {
              key: 'active',
              label: t('columns.status'),
              width: 120,
              render: (row) => (row.active ? t('status.active') : t('status.inactive')),
              sortable: true,
              sortValue: (row) => (row.active ? 1 : 0),
            },
            {
              key: 'contacts',
              label: t('columns.contacts'),
              render: (row) => [row.email, row.phone].filter(Boolean).join(' / ') || '-',
            },
            {
              key: 'notes',
              label: t('columns.notes'),
              render: (row) => <NotesCell value={row.notes} />,
            },
            {
              key: 'warehouses',
              label: t('columns.warehouses'),
              width: 110,
              align: 'right',
              render: (row) => row.warehouses.length,
              sortable: true,
              sortValue: (row) => row.warehouses.length,
            },
            {
              key: 'priceLists',
              label: t('columns.priceLists'),
              width: 110,
              align: 'right',
              render: (row) => row.priceLists.length,
              sortable: true,
              sortValue: (row) => row.priceLists.length,
            },
          ]}
        />
      )}
      {createOpen && <CarrierCreateModal onClose={() => setCreateOpen(false)} />}
      {selected && (
        <CarrierEditorModal
          carrier={selected}
          onClose={() => setSelected(null)}
          onUpdated={(carrier) => setSelected(carrier)}
        />
      )}
    </div>
  );
}

async function listForFilter(filter: CarrierFilter, q: string): Promise<Carrier[]> {
  const query = q.trim() ? { q: q.trim(), limit: 100 } : { limit: 100 };
  if (filter === 'active') return (await listCarriers({ ...query, active: true })).items;
  if (filter === 'inactive') return (await listCarriers({ ...query, active: false })).items;
  const [active, inactive] = await Promise.all([
    listCarriers({ ...query, active: true }),
    listCarriers({ ...query, active: false }),
  ]);
  return [...active.items, ...inactive.items];
}

function emptyCarrierForm(): CreateCarrierInput {
  return { name: '', email: null, phone: null, notes: '', active: true };
}

function normalizeCarrierForm(form: CreateCarrierInput): CreateCarrierInput {
  return {
    name: form.name.trim(),
    email: form.email?.trim() ? form.email.trim() : null,
    phone: form.phone?.trim() ? form.phone.trim() : null,
    notes: form.notes.trim(),
    active: form.active,
  };
}

function CarrierCreateModal({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation(['carriers', 'common']);
  const qc = useQueryClient();
  const showToast = useToastStore((s) => s.show);
  const [form, setForm] = useState<CreateCarrierInput>(emptyCarrierForm);
  // INVARIANT: dirty compares the normalized form against the normalized
  // empty baseline so leading/trailing whitespace in the still-empty form
  // does not falsely trigger the unsaved-changes prompt. A successful
  // create resets `form` back to the empty defaults via the mutation's
  // onSuccess handler so the guard does not warn after the toast/close.
  const mutation = useMutation({
    mutationFn: createCarrier,
    onSuccess: () => {
      setForm(emptyCarrierForm());
      void qc.invalidateQueries({ queryKey: ['carriers-catalog'] });
      showToast({ tone: 'success', text: t('toasts.created') });
      onClose();
    },
    onError: (err) => showToast({ tone: 'error', text: errorMessage(err) }),
  });
  const dirty = isCarrierFormDirty(form);

  return (
    <EditorModal
      open
      title={t('modals.createTitle')}
      width={520}
      dirty={dirty}
      busy={mutation.isPending}
      onConfirmClose={onClose}
    >
      <CarrierForm form={form} onChange={setForm} />
      <EditorFooter>
        <CancelButton onClose={onClose} label={t('common:actions.cancel')} />
        <Button
          kind="primary"
          disabled={!form.name.trim() || mutation.isPending}
          onClick={() => mutation.mutate(normalizeCarrierForm(form))}
        >
          {t('common:actions.create')}
        </Button>
      </EditorFooter>
    </EditorModal>
  );
}

// WHY: Cancel must call `useEditorGuard().confirmDestructive(onClose)` to
// prompt on dirty close. The EditorGuardContext provider only wraps the
// EditorModal `children` subtree, not the `footer` prop, so the action
// buttons are rendered inline at the bottom of children. `EditorFooter`
// mirrors the visual style of the Modal footer slot.
function EditorFooter({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        marginTop: 12,
        marginLeft: -16,
        marginRight: -16,
        marginBottom: -16,
        padding: '12px 16px',
        borderTop: '1px solid var(--border-2)',
        display: 'flex',
        gap: 8,
        justifyContent: 'flex-end',
      }}
    >
      {children}
    </div>
  );
}

function CancelButton({ onClose, label }: { onClose: () => void; label: string }) {
  const { confirmDestructive } = useEditorGuard();
  return (
    <Button kind="ghost" onClick={() => confirmDestructive(onClose)}>
      {label}
    </Button>
  );
}

// WHY: meaningful dirty for create = normalized form differs from
// normalized empty defaults. Trimming via `normalizeCarrierForm` means
// pure whitespace edits do not trigger the guard, matching user intent.
function isCarrierFormDirty(form: CreateCarrierInput): boolean {
  const current = normalizeCarrierForm(form);
  const baseline = normalizeCarrierForm(emptyCarrierForm());
  return (
    current.name !== baseline.name ||
    current.email !== baseline.email ||
    current.phone !== baseline.phone ||
    current.notes !== baseline.notes ||
    current.active !== baseline.active
  );
}

function CarrierForm({
  form,
  onChange,
}: {
  form: CreateCarrierInput;
  onChange: (form: CreateCarrierInput) => void;
}) {
  const { t } = useTranslation('carriers');
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
      <Labeled label={t('fields.name')} required>
        <Input value={form.name} onChange={(name) => onChange({ ...form, name })} />
      </Labeled>
      <Labeled label={t('fields.email')}>
        <Input
          value={form.email ?? ''}
          onChange={(email) => onChange({ ...form, email })}
          placeholder={t('fields.optional')}
        />
      </Labeled>
      <Labeled label={t('fields.phone')}>
        <Input
          value={form.phone ?? ''}
          onChange={(phone) => onChange({ ...form, phone })}
          placeholder={t('fields.optional')}
        />
      </Labeled>
      <div />
      <div style={{ gridColumn: '1 / -1' }}>
        <Labeled label={t('fields.notes')}>
          <Textarea
            value={form.notes}
            onChange={(notes) => onChange({ ...form, notes })}
            placeholder={t('fields.optional')}
          />
        </Labeled>
      </div>
    </div>
  );
}

function Labeled({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 5, fontSize: 12 }}>
      <span style={{ color: 'var(--fg-3)' }}>
        {label}
        {required ? ' *' : ''}
      </span>
      {children}
    </label>
  );
}

// WHY: notes can be free-form long text. One-line truncation + native title
// keeps the desktop table row height stable while preserving the full value
// for hover read-out, and the narrow card path inherits the same shrink
// behavior so a single long unbreakable token never widens the viewport.
function NotesCell({ value }: { value: string }) {
  const trimmed = value.trim();
  if (!trimmed) return <span style={{ color: 'var(--fg-3)' }}>-</span>;
  return (
    <span
      title={trimmed}
      style={{
        display: 'block',
        minWidth: 0,
        maxWidth: '100%',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
    >
      {trimmed}
    </span>
  );
}
